import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useMangoApiClient } from '~/atoms/MangoApi'
import {
  mapUiStatusToApiStatus,
  MissedCall,
  MissedCallApiResponse,
  transformMissedCallApiResponse,
} from '~/pages/MissedCalls/types/MissedCall'
import { getPaginatedResultParts } from '~/utils/apiQueryUtils'

import {
  ApiError,
  Call,
  CallsQueryParams,
  PaginatedApiResult,
} from '@mangovoice/types'

const DEFAULT_MISSED_CALLS_LIMIT = 25

// Only the fields we actually support updating in the cache
type MissedCallUpdatableFields = Partial<
  Pick<
    MissedCall,
    'follow_up_status' | 'assigned_to_user_id' | 'follow_up_time_utc'
  >
>

export const useGetInfiniteMissedCalls = (
  queryParams: Partial<CallsQueryParams> = {},
  options?: Omit<
    UseInfiniteQueryOptions<
      PaginatedApiResult<Call>,
      ApiError,
      InfiniteData<PaginatedApiResult<Call>, number>,
      QueryKey,
      number
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) => {
  const api = useMangoApiClient()
  const queryClient = useQueryClient()

  const queryKey = useMemo(
    () => ['infinite-missed-calls', queryParams],
    [queryParams],
  )

  const infiniteQuery = useInfiniteQuery<
    PaginatedApiResult<Call>,
    ApiError,
    InfiniteData<PaginatedApiResult<Call>, number>,
    QueryKey,
    number
  >({
    queryKey,
    queryFn: async ({ signal, pageParam = 0 }) => {
      const { ok, data, status } = await api.calls.getMissedCalls(
        {
          ...(queryParams as Record<string, unknown>),
          limit: queryParams.limit ?? DEFAULT_MISSED_CALLS_LIMIT,
          offset: pageParam as number,
          ordering: queryParams.ordering
            ? queryParams.ordering.map(String)
            : ['-started_at'],
          started_after:
            queryParams.started_after instanceof Date
              ? queryParams.started_after.toISOString()
              : queryParams.started_after,
          started_before:
            queryParams.started_before instanceof Date
              ? queryParams.started_before.toISOString()
              : queryParams.started_before,
        } as Parameters<typeof api.calls.getMissedCalls>[0],
        { signal },
      )

      if (!ok) {
        const error = { ...(data as object), status }
        return Promise.reject(error)
      }

      return data!
    },
    getNextPageParam: (lastPage) => {
      const parts = getPaginatedResultParts(lastPage)
      return parts?.hasNextPage ? parts.nextPageOffset : undefined
    },
    initialPageParam: 0,
    ...options,
  })

  const refetch = useCallback(() => {
    infiniteQuery.refetch()
  }, [infiniteQuery])

  // Type guard to check if data has pages property (InfiniteData structure)
  const hasPages = (
    data:
      | PaginatedApiResult<Call>
      | InfiniteData<PaginatedApiResult<Call>, number>
      | undefined,
  ): data is InfiniteData<PaginatedApiResult<Call>, number> => {
    return data !== undefined && 'pages' in data
  }

  // Transform raw Call objects to MissedCall objects
  const results = useMemo(() => {
    const data = infiniteQuery.data
    if (!hasPages(data)) {
      return []
    }
    const rawCalls = data.pages.flatMap((page) => page.results)
    return rawCalls.map((call) =>
      transformMissedCallApiResponse(call as MissedCallApiResponse),
    )
  }, [infiniteQuery.data])

  const totalCount = useMemo(() => {
    const data = infiniteQuery.data
    if (!hasPages(data)) {
      return 0
    }
    return data.pages[0]?.count ?? 0
  }, [infiniteQuery.data])

  // Cache utility for updating a missed call in the cache
  const updateMissedCallInCache = useCallback(
    (missedCallId: number, updates: MissedCallUpdatableFields) => {
      queryClient.setQueryData<InfiniteData<PaginatedApiResult<Call>, number>>(
        queryKey,
        (oldData) => {
          if (!oldData) return undefined

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              results: page.results.map((call) => {
                if (call.id !== missedCallId) {
                  return call
                }

                type CallWithTracking = Call & {
                  missed_call_tracking?: {
                    follow_up_status?: string
                    follow_up_user?: number | null
                    follow_up_time_utc?: string | null
                  }
                  missed_call_return_status?: string
                }

                const updatedCall: CallWithTracking = {
                  ...call,
                }
                const {
                  follow_up_status,
                  assigned_to_user_id,
                  follow_up_time_utc,
                } = updates

                const trackingUpdates: {
                  follow_up_status?: string
                  follow_up_user?: number | null
                  follow_up_time_utc?: string | null
                } = {}

                if (follow_up_status !== undefined) {
                  const apiStatus = mapUiStatusToApiStatus(follow_up_status)
                  trackingUpdates.follow_up_status = apiStatus
                  updatedCall.missed_call_return_status = apiStatus
                }

                if (assigned_to_user_id !== undefined) {
                  trackingUpdates.follow_up_user = assigned_to_user_id
                }

                if (follow_up_time_utc !== undefined) {
                  trackingUpdates.follow_up_time_utc = follow_up_time_utc
                }

                if (Object.keys(trackingUpdates).length > 0) {
                  return {
                    ...updatedCall,
                    missed_call_tracking: {
                      ...(updatedCall.missed_call_tracking ?? {}),
                      ...trackingUpdates,
                    },
                  } as CallWithTracking
                }

                // No tracking-related changes; return the call as-is
                return updatedCall
              }),
            })),
          }
        },
      )
    },
    [queryKey, queryClient],
  )

  return {
    ...infiniteQuery,
    results,
    refetch,
    totalCount,
    updateMissedCallInCache,
  }
}

export const useUpdateMissedCall = () => {
  const api = useMangoApiClient()

  return useMutation({
    mutationFn: async (payload: {
      call_id: number
      follow_up_status: string
      follow_up_user: number | null
      follow_up_time_utc: string
      pbx: number
    }) => {
      const { ok, data, status } = await api.calls.updateMissedCall(payload)
      if (!ok) {
        const error = { ...(data as object), status }
        return Promise.reject(error)
      }
      return data!
    },
  })
}
